import { Editor } from "@tiptap/core";
import { PathMetadata, RelevantProperty, Suggestion } from "./types";
import { getContextData, getGlobalContext } from "./contextRegistry";

const getSuggestionTokenSegment = (suggestion: Suggestion): string => {
    const segment = suggestion.insertSegment ?? suggestion.displayText.replace(/\[\]$/, "");
    return suggestion.isArray ? `${segment}[0]` : segment;
};

const findPathMetadata = (allProperties: PathMetadata[], path: string): PathMetadata | undefined => {
    return allProperties.find((property) => property.path === path);
};

const segmentMatchesPartialQuery = (segment: string, query: string, metadata?: PathMetadata): boolean => {
    const normalizedQuery = query.toLowerCase();
    if (segment.toLowerCase().startsWith(normalizedQuery)) {
        return true;
    }

    const displayLabel = metadata?.displayLabel;
    return displayLabel !== undefined && displayLabel.toLowerCase().startsWith(normalizedQuery);
};

const toRelevantProperty = (
    segment: string,
    isArray: boolean,
    allProperties: PathMetadata[],
    fullPath: string,
): RelevantProperty => {
    const metadata = findPathMetadata(allProperties, fullPath);
    const displayText = metadata?.displayLabel ?? segment;

    return {
        name: segment,
        isArray,
        displayText: isArray ? `${displayText}[]` : displayText,
        description: metadata?.description,
    };
};

const getSuggestionTokenPath = (suggestion: Suggestion): string => {
    return suggestion.isArray ? `${suggestion.text}[0]` : suggestion.text;
};

export const insertSuggestion = (
    suggestion: Suggestion,
    isNamespaceSelected: boolean,
    editor: Editor,
    range: { from: number; to: number } | null,
    appendDot = false,
) => {
    const { state, dispatch } = editor.view;

    if (!range) return;

    const currentText = state.doc.textBetween(range.from, range.to);
    const suffix = appendDot ? "." : "}";

    if (isNamespaceSelected) {
        const tokenMatch = currentText.match(/^\{([^}]*)$/);
        if (tokenMatch) {
            const currentQuery = tokenMatch[1];
            const parts = currentQuery.split(".");

            if (parts.length > 1) {
                const lastDotIndex = currentQuery.lastIndexOf(".");
                const beforeLastDot = currentQuery.substring(0, lastDotIndex + 1);
                const replacement = `{${beforeLastDot}${getSuggestionTokenSegment(suggestion)}${suffix}`;

                dispatch(state.tr.replaceWith(range.from, range.to, state.schema.text(replacement)).scrollIntoView());
            } else {
                const replacement = `{${getSuggestionTokenPath(suggestion)}${suffix}`;
                dispatch(state.tr.replaceWith(range.from, range.to, state.schema.text(replacement)).scrollIntoView());
            }
        } else {
            const replacement = `{${getSuggestionTokenPath(suggestion)}${suffix}`;
            dispatch(state.tr.replaceWith(range.from, range.to, state.schema.text(replacement)).scrollIntoView());
        }
    } else {
        const replacement = `{${getSuggestionTokenPath(suggestion)}.`;
        dispatch(state.tr.replaceWith(range.from, range.to, state.schema.text(replacement)).scrollIntoView());
    }
};

export const getNamespaceSuggestions = (query?: string): Suggestion[] => {
    const globalContext = getGlobalContext();
    const dynamicNamespaces = Object.keys(globalContext);
    const allNamespaces = [...new Set([...dynamicNamespaces])];

    const filteredNamespaces =
        query ?
            allNamespaces.filter((namespace) => namespace.toLowerCase().startsWith(query.toLowerCase()))
        :   allNamespaces;

    return filteredNamespaces.map((namespace) => ({
        text: namespace,
        displayText: namespace,
    }));
};

export const getRelevantProperties = (
    namespace: string,
    propertyPath: string,
    justTypedDot: boolean,
): RelevantProperty[] => {
    const allProperties = getContextData(namespace);

    const cleanArrayNotation = (path: string): string => {
        return path.replace(/\[(\d+|\w+)\]/g, "");
    };

    if (propertyPath === "") {
        const firstLevelProps = new Map<string, boolean>();

        allProperties.forEach((prop) => {
            const pathParts = prop.path.split(".");
            if (pathParts.length === 1) {
                firstLevelProps.set(pathParts[0], prop.isArray);
            }
        });

        return Array.from(firstLevelProps)
            .map(([name, isArray]) => toRelevantProperty(name, isArray, allProperties, name))
            .sort((a, b) => (a.displayText ?? a.name).localeCompare(b.displayText ?? b.name));
    }

    if (justTypedDot) {
        const childrenMap = new Map<string, boolean>();

        const cleanParentPath = cleanArrayNotation(propertyPath);
        const cleanParentPrefix = cleanParentPath ? cleanParentPath + "." : "";

        allProperties.forEach((prop) => {
            const cleanPropPath = cleanArrayNotation(prop.path);

            if (!cleanPropPath.startsWith(cleanParentPrefix)) {
                return;
            }

            const relativePath = cleanPropPath.substring(cleanParentPrefix.length);
            const nextSegment = relativePath.split(".")[0];

            if (nextSegment && !relativePath.substring(nextSegment.length + 1).includes(".")) {
                const fullCleanPath = cleanParentPath + "." + nextSegment;
                const matchingProp = allProperties.find((p) => cleanArrayNotation(p.path) === fullCleanPath);
                const isArray = matchingProp?.isArray || false;

                childrenMap.set(nextSegment, isArray);
            }
        });

        return Array.from(childrenMap)
            .map(([name, isArray]) => {
                const fullCleanPath = cleanParentPath ? `${cleanParentPath}.${name}` : name;
                return toRelevantProperty(name, isArray, allProperties, fullCleanPath);
            })
            .sort((a, b) => (a.displayText ?? a.name).localeCompare(b.displayText ?? b.name));
    }

    const cleanPropertyPath = cleanArrayNotation(propertyPath);
    const segments = cleanPropertyPath.split(".");
    const lastSegment = segments.pop() || "";
    const parentPath = segments.join(".");
    const parentPrefix = parentPath ? parentPath + "." : "";

    const childrenMap = new Map<string, boolean>();

    allProperties.forEach((prop) => {
        if (!parentPath) {
            if (!prop.path.includes(".")) {
                const metadata = findPathMetadata(allProperties, prop.path);
                if (segmentMatchesPartialQuery(prop.path, lastSegment, metadata)) {
                    childrenMap.set(prop.path, prop.isArray);
                }
            }
        } else {
            const cleanedPropPath = cleanArrayNotation(prop.path);

            if (!cleanedPropPath.startsWith(parentPrefix)) return;

            const propRelativeToParent = cleanedPropPath.substring(parentPrefix.length);
            const nextSegment = propRelativeToParent.split(".")[0];

            if (propRelativeToParent && nextSegment) {
                const isDirectChild = !propRelativeToParent.substring(nextSegment.length + 1).includes(".");

                if (isDirectChild) {
                    const fullPropertyPath = `${parentPath}.${nextSegment}`;
                    const matchingProperty = allProperties.find((p) => cleanArrayNotation(p.path) === fullPropertyPath);
                    const isArrayAtThisLevel = matchingProperty ? matchingProperty.isArray : false;

                    if (lastSegment === "" || segmentMatchesPartialQuery(nextSegment, lastSegment, matchingProperty)) {
                        childrenMap.set(nextSegment, isArrayAtThisLevel);
                    }
                }
            }
        }
    });

    return Array.from(childrenMap)
        .map(([name, isArray]) => {
            const fullPropertyPath = parentPath ? `${parentPath}.${name}` : name;
            return toRelevantProperty(name, isArray, allProperties, fullPropertyPath);
        })
        .sort((a, b) => (a.displayText ?? a.name).localeCompare(b.displayText ?? b.name));
};
